import { PaymentContext, isPaymentAvailable } from "../models/payment-availability.model";
import { inject, Injectable, signal } from "@angular/core";
import { DertApiService } from "./api/dert.service";
import { IGetPaymentTypesResultData } from "./model/pos.api.model";
import { SharedService } from "./shared.service";
import { TranslateService } from "@ngx-translate/core";
import { TenderType } from "./model/tender-type.enum";

@Injectable({
  providedIn: "root",
})
export class PaymentTypesService {
  private _dertApi = inject(DertApiService);
  private _sharedService = inject(SharedService);
  private _translate = inject(TranslateService);

  public benefitPaymentEnabled = signal(false);
  private paymentNominalsCache: Promise<any> | null = null;
  private maximumPaymentsCache: any | null = null;
  private maximumPaymentsLoaded = false;

  constructor() {}

  async getPaymentTypesWithNominals(context?: PaymentContext): Promise<IGetPaymentTypesResultData[]> {
    // Returns payment types with their nominal values
    const allowedPayments = context ? null : await this.getAllowedPayments();
    if (!context && !allowedPayments) return null;

    const paymentTypes = await this._sharedService.getPaymentTypes();
    if (!paymentTypes) return null;

    const paymentNominals = await this.getPaymentsNominals();
    const filteredAllowedPayments = context
      ? paymentTypes.filter(payment => isPaymentAvailable(payment, context))
      : this.filterAllowedPayments(paymentTypes, allowedPayments);
    const filteredPaymentsWithNominals = this.formatPaymentTypes(filteredAllowedPayments, paymentNominals);

    return filteredPaymentsWithNominals;
  }

  async getBenefitTypesWithNominals(context?: PaymentContext): Promise<IGetPaymentTypesResultData[]> {
    const paymentTypes = await this.getPaymentTypesWithNominals(context);
    if (!paymentTypes) return [];

    const filteredBenefits = paymentTypes.filter(
      (payment) => (
        payment.TenderType === TenderType.ElectronicVoucher ||
        payment.TenderType === TenderType.Cheque ||
        payment.TenderType === TenderType.Virtual
      ) && payment.IsBenefit,
    );

    if (filteredBenefits.length === 0) {
      console.warn("[PaymentTypesService] No benefit payment types found; benefit payment is disabled.", context);
      this.benefitPaymentEnabled.set(false);
      return [];
    }
    this.benefitPaymentEnabled.set(true);

    filteredBenefits.sort((a, b) => b.TenderType - a.TenderType);

    return filteredBenefits;
  }

  

  public async getMaxCashPayment(): Promise<any> {
    if (this.maximumPaymentsLoaded) {
      return this.maximumPaymentsCache;
    }

    // From MaximumPaymentConfig config get allowed payments for the current country
    const configType = "NG.Shared.Models.Config.PaymentsConfig"; // For your own sanity, don't look or ask why!
    const res = await this._dertApi.loadConfig(configType);
    if (!res || !res.config) {
      await this._sharedService.throwErrorDialog(
        this._translate.instant("webPos.Errors.NoAllowedPaymentsFound"),
        null,
        true
      );
      return null;
    }
    const maxPaymentConfig = res.config.maximumPaymentConfig;
    this.maximumPaymentsCache = maxPaymentConfig;
    this.maximumPaymentsLoaded = true;
    return maxPaymentConfig;
  }


  private async getAllowedPayments(): Promise<any> {
    // From PaymentTypeDisplayLayoutConfig config get allowed payments for the current country
    const configType = "NG.Shared.Models.Config.PaymentTypeDisplayLayoutConfig"; // For your own sanity, don't look or ask why!
    const res = await this._dertApi.loadConfig(configType);
    if (!res || !res.config) {
      await this._sharedService.throwErrorDialog(
        this._translate.instant("webPos.Errors.NoAllowedPaymentsFound"),
        null,
        true
      );
      return null;
    }
    const allowedPayments = res.config.records?.map((record) => {
      if (record?.paymentTypeName) {
        return record.paymentTypeName;
      }
    });
    return allowedPayments;
  }


  public async getPaymentsNominals(): Promise<any> {
    // Return cached promise if already in progress or completed
    if (this.paymentNominalsCache) {
      return this.paymentNominalsCache;
    }

    // Create the promise and cache it
    this.paymentNominalsCache = (async () => {
      // From TenderDenominationListConfig config get nominals for payments
      const configType = "NG.Shared.Models.Config.TenderDenominationListConfig"; // For your own sanity, don't look or ask why!
      const res = await this._dertApi.loadConfig(configType);

      if (!res || !res.config) {
        await this._sharedService.throwErrorDialog(
          this._translate.instant("webPos.Errors.NoAllowedPaymentsFound"),
          null
        );
        return null;
      }

      const nominals = res.config.tenderDenominations?.map((tender) => {
        if (tender?.name && tender?.records && tender?.paymentTypes && tender.paymentTypes.length) {
          return {
            paymentType: tender.paymentTypes[0],
            nominals: tender.records,
          };
        }
        return null;
      });
      return nominals;
    })();

    return this.paymentNominalsCache;
  }

  public async getCashDenominations(paymentType: string): Promise<any[]> {
    // Get denominations for a specific payment type (e.g., "CASH CZ")
    const nominals = await this.getPaymentsNominals();
    if (!nominals || nominals.length === 0) {
      return [];
    }

    const cashNominals = nominals.find((n) => n?.paymentType === paymentType);
    return cashNominals?.nominals || [];
  }

  private filterAllowedPayments(
    allPayments: any[],
    allowedPayments: any[]
  ): any[] {
    // Filter payments from payment types on base of allowed payments
    return allPayments.filter((payment) =>
      allowedPayments.includes(payment.Name)
    );
  }

  private filterNominals(): any[] {
    // Filter nominals for loaded payments types
    return [];
  }

  private formatPaymentTypes(
    filteredAllowedPayments: any[],
    nominals: any[]
  ): IGetPaymentTypesResultData[] {
    // Format payments and add their nominals to it
    return filteredAllowedPayments.map((payment) => {
      const paymentNominals = (nominals ?? []).filter(Boolean).find(
        (n) => n.paymentType === payment.Name
      );
      return {
        ...payment,
        Nominals: paymentNominals ? paymentNominals.nominals : [],
      };
    });
  }
}
